import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { api, CATEGORIES, errMsg } from "@/lib/api";
import { Eyebrow, Reveal } from "@/components/site/Motion";

export const Field = ({ label, children, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="mb-2 block font-mono text-[11px] uppercase tracking-[0.18em] text-haze">{label}</span>
    {children}
  </label>
);

export const inputCls = "w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 text-foreground outline-none transition-[border-color,background-color] duration-300 placeholder:text-haze/60 focus:border-flash focus:bg-white/[0.06]";

const EMPTY = { name: "", email: "", phone: "", company: "", event_name: "", category: "social", date: "", location: "", expected_people: "", price_per_photo: "", message: "" };

export const ProposalForm = () => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/event-requests", { ...f, price_per_photo: Number(f.price_per_photo || 0) });
      toast.success("Proposta enviada! Entraremos em contato para confirmar.");
      setF(EMPTY);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="proponha" className="relative overflow-hidden border-t border-white/10 bg-ink-2/50 py-24 lg:py-32" data-testid="proposal-section">
      <div aria-hidden className="pointer-events-none absolute -right-40 top-10 h-[480px] w-[480px] rounded-full bg-violet/25 blur-[140px]" />
      <div className="relative mx-auto grid max-w-[1400px] gap-14 px-5 sm:px-8 lg:grid-cols-12">
        <Reveal className="lg:col-span-5">
          <Eyebrow>Para organizadores</Eyebrow>
          <h2 className="mt-5 font-display text-4xl font-bold leading-[0.95] tracking-tight sm:text-6xl">Proponha seu evento.</h2>
          <p className="mt-6 max-w-md text-haze md:text-lg">
            Conte sobre o evento e sugira o valor por foto para os participantes. Nossa equipe confirma o acordo e escala um fotógrafo associado — sem custo para você.
          </p>
          <div className="mt-10 overflow-hidden rounded-[1.5rem] border border-white/10">
            <img src="/img/gala.jpg" alt="Evento fotografado" className="h-56 w-full object-cover" />
          </div>
        </Reveal>
        <Reveal className="lg:col-span-7" delay={0.1}>
          <form onSubmit={submit} className="grid gap-5 rounded-[1.75rem] border border-white/10 bg-ink/70 p-6 backdrop-blur-xl sm:grid-cols-2 sm:p-10" data-testid="proposal-form">
            <Field label="Seu nome"><input required value={f.name} onChange={set("name")} className={inputCls} data-testid="proposal-name-input" /></Field>
            <Field label="Empresa / organização"><input value={f.company} onChange={set("company")} className={inputCls} data-testid="proposal-company-input" /></Field>
            <Field label="E-mail"><input required type="email" value={f.email} onChange={set("email")} className={inputCls} data-testid="proposal-email-input" /></Field>
            <Field label="WhatsApp"><input required value={f.phone} onChange={set("phone")} placeholder="(11) 90000-0000" className={inputCls} data-testid="proposal-phone-input" /></Field>
            <Field label="Nome do evento" className="sm:col-span-2"><input required value={f.event_name} onChange={set("event_name")} className={inputCls} data-testid="proposal-event-name-input" /></Field>
            <Field label="Tipo">
              <select value={f.category} onChange={set("category")} className={inputCls} data-testid="proposal-category-select">
                {CATEGORIES.map((c) => <option key={c.id} value={c.id} className="bg-ink">{c.label}</option>)}
              </select>
            </Field>
            <Field label="Data"><input required type="date" value={f.date} onChange={set("date")} className={`${inputCls} [color-scheme:dark]`} data-testid="proposal-date-input" /></Field>
            <Field label="Local"><input required value={f.location} onChange={set("location")} className={inputCls} data-testid="proposal-location-input" /></Field>
            <Field label="Público estimado"><input value={f.expected_people} onChange={set("expected_people")} placeholder="ex.: 300" className={inputCls} data-testid="proposal-people-input" /></Field>
            <Field label="Valor sugerido por foto (R$)" className="sm:col-span-2">
              <input required type="number" min="0" step="0.5" value={f.price_per_photo} onChange={set("price_per_photo")} placeholder="ex.: 15" className={inputCls} data-testid="proposal-price-input" />
            </Field>
            <Field label="Mensagem" className="sm:col-span-2"><textarea rows={3} value={f.message} onChange={set("message")} className={inputCls} data-testid="proposal-message-input" /></Field>
            <button disabled={busy} type="submit" data-testid="proposal-submit-btn"
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-flash px-7 py-4 font-semibold text-ink transition-[background-color,transform] duration-300 hover:scale-[1.02] hover:bg-flash-2 disabled:opacity-60 sm:col-span-2 sm:justify-self-start">
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />} Enviar proposta
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
